import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { initPlayer } from "../../components/Radio/initPlayer";
import { AAC_STREAM_URL, DEFAULT_VOLUME, HLS_STREAM_URL } from "../components/Radio/radioStream";

export const RADIO_STATUS = Object.freeze({ IDLE: "idle", PLAYING: "playing", PAUSED: "paused" });

const noop = () => {};

const IDLE_PLAYBACK = Object.freeze({
  status: RADIO_STATUS.IDLE,
  play: noop,
  pause: noop,
  toggle: noop,
  stop: noop,
});

const RadioPlaybackContext = createContext(IDLE_PLAYBACK);

export function RadioPlaybackProvider({ children }) {
  const audioRef = useRef(null);
  const streamRef = useRef(null);
  const attemptRef = useRef(0);
  const [status, setStatus] = useState(RADIO_STATUS.IDLE);

  useEffect(() => {
    const audio = audioRef.current;
    return () => {
      if (streamRef.current) audio?.pause();
    };
  }, []);

  const ensureStream = useCallback((audio) => {
    if (!streamRef.current) {
      audio.volume = DEFAULT_VOLUME;
      streamRef.current = initPlayer(HLS_STREAM_URL, audio).then((hlsAttached) => {
        if (!hlsAttached) audio.src = AAC_STREAM_URL;
      });
    }
    return streamRef.current;
  }, []);

  const play = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    attemptRef.current += 1;
    const attempt = attemptRef.current;
    setStatus(RADIO_STATUS.PLAYING);
    // A pause or stop issued while the stream is still attaching must win over this play.
    ensureStream(audio)
      .then(() => (attemptRef.current === attempt ? audio.play() : undefined))
      .catch(() => {
        if (attemptRef.current === attempt) setStatus(RADIO_STATUS.IDLE);
      });
  }, [ensureStream]);

  const halt = useCallback((next) => {
    attemptRef.current += 1;
    audioRef.current?.pause();
    setStatus(next);
  }, []);

  const pause = useCallback(
    () => halt((current) => (current === RADIO_STATUS.IDLE ? current : RADIO_STATUS.PAUSED)),
    [halt]
  );

  const stop = useCallback(() => halt(RADIO_STATUS.IDLE), [halt]);

  const toggle = useCallback(
    () => (status === RADIO_STATUS.PLAYING ? pause() : play()),
    [status, pause, play]
  );

  const syncPaused = () => setStatus((current) => (current === RADIO_STATUS.PLAYING ? RADIO_STATUS.PAUSED : current));
  const syncPlaying = () => setStatus((current) => (current === RADIO_STATUS.PAUSED ? RADIO_STATUS.PLAYING : current));

  const value = useMemo(() => ({ status, play, pause, toggle, stop }), [status, play, pause, toggle, stop]);

  return (
    <RadioPlaybackContext.Provider value={value}>
      {children}
      <audio ref={audioRef} hidden preload="none" onPause={syncPaused} onPlay={syncPlaying} />
    </RadioPlaybackContext.Provider>
  );
}

RadioPlaybackProvider.propTypes = {
  children: PropTypes.node,
};

export const useRadioPlayback = () => useContext(RadioPlaybackContext);
